
import { Component, Mixins, Prop, Provide } from "vue-property-decorator";
import { mapState } from "vuex";
import { CreateElement } from "vue";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import BrandsStore from "@/mixins/brandsStore";

// Enums/Data
import { ActionTypes } from "@/data/enums";
import { DataModules } from "@/store/modules/data";
import { DiscountTypes } from "@/data/enums/promotions";
import { ProductTypes } from "@/data/enums/products";

// Types
import { IState } from "@/store";
import {
  IPromotionProviderData,
  IPromotionProviderMethods
} from "@/models/providers/promotions";
import { IPromotion } from "@/models/promotions";
import { IProduct } from "@/models/products";
import { IProvider } from "@/models/providers";

@Component({
  computed: {
    ...mapState({
      promotionScope(state: IState, getters) {
        return getters[
          `data/${DataModules.CATALOGUE_PROMOTIONS}/promotionScope`
        ](this.promotionId);
      },
      promotion(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.CATALOGUE_PROMOTIONS,
          scope: this.savedPromotionScope
        });
      }
    })
  }
})
export default class PromotionsProvider
  extends Mixins(RequestPromises, BrandsStore)
  implements IProvider {
  // Provide Data
  @Provide("promotionProviderData")
  public providerData(): IPromotionProviderData {
    return {
      isUpdate: this.isUpdate,
      promotion: this.promotion,
      promotionProducts: this.promotionProducts,
      isLoading: this.isLoading,
      isReloading: this.isReloading,
      hasRequestError: this.hasRequestError
    };
  }

  // Provide Methods
  @Provide("promotionProviderMethods")
  public providerMethods: IPromotionProviderMethods = {
    getPromotion: this.getPromotion,
    getNewProducts: this.getNewProducts,
    reloadData: this.reloadData
  };

  // Props
  @Prop({ default: ActionTypes.CREATE })
  public actionType!: ActionTypes;

  @Prop({ default: true })
  public handleLoading!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ default: true })
  public handleRequestError!: boolean;

  // Map State
  public promotionScope!: string;
  public promotion!: IPromotion;
  public promotionProducts!: IProduct[];

  // Data
  public isLoading = false;
  public isReloading = false;
  public hasRequestError = false;

  public savedPromotionScope = "";
  public savedPromotionProductsScope = "";

  public discountTypes = DiscountTypes;

  public productTypes: ProductTypes[] = [
    ProductTypes.SINGLE_PRODUCT,
    ProductTypes.PRODUCT_BUNDLE
  ];

  // Lifecycle methods
  public async created() {
    this.isLoading = true;
    return this.loadData();
  }

  public beforeDestroy() {
    if (this.savedPromotionScope) {
      this.$store.dispatch("data/binSingle", {
        storeModule: DataModules.CATALOGUE_PROMOTIONS,
        scope: this.savedPromotionScope,
        vm: this
      });
    }

    this.$store.dispatch("data/binList", {
      storeModule: DataModules.CATALOGUE,
      scope: this.savedPromotionProductsScope,
      vm: this
    });
  }

  // Getters
  public get promotionProductsIds() {
    return this.$_.concat(
      this.$_.map(
        this.$_.get(this.promotion, "products", []),
        product => product.product_id
      ),
      this.$_.map(
        this.$_.get(this.promotion, "mandatory_products", []),
        product => product.product_id
      )
    );
  }

  public get promotionId() {
    return this.$route.params.promotionId;
  }

  public get isUpdate() {
    return this.actionType === ActionTypes.UPDATE && !!this.promotionId;
  }

  // Methods
  public async loadData() {
    try {
      if (this.isUpdate) {
        this.savedPromotionScope = this.promotionScope;
        this.savedPromotionProductsScope = `$promotion_${this.promotionId}_products`;
        await this.getPromotion(this.isReloading);

        if (this.brandId !== this.$_.get(this.promotion, "brand_id")) {
          this.$store.dispatch("toast/show", {
            message: this.$t("_.promotion_from_different_brand")
          });
          this.$router.push({
            name: "adminPromotionList"
          });
        }
      }

      this.hasRequestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.hasRequestError = true;
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    return this.loadData();
  }

  public getPromotion(ignoreStored = false) {
    return this.$store.dispatch(
      `data/${DataModules.CATALOGUE_PROMOTIONS}/get`,
      {
        id: this.promotionId,
        scope: this.savedPromotionScope,
        params: {
          with: [
            "currencies",
            "billing_cycles",
            "pricelists",
            "products",
            "products.currencies",
            "products.billing_cycles",
            "products.pricelists",
            "mandatory_products",
            "mandatory_products.currencies",
            "mandatory_products.billing_cycles",
            "mandatory_products.pricelists"
          ].join(",")
        },
        vm: this,
        ignoreStored
      }
    );
  }

  public getNewProducts(newProductsIds: string) {
    return this.$store.dispatch(`data/${DataModules.CATALOGUE}/list`, {
      params: {
        limit: 0,
        "filter[id|like]": newProductsIds,
        with: "image"
      },
      storeData: false,
      returnData: true,
      vm: this
    });
  }

  public openAttachIndividualProduct(events) {
    this.$store.dispatch(
      `data/${DataModules.CATALOGUE_PROMOTIONS}/openManageProductModal`,
      {
        props: {
          promotionBrandId: this.brandId
        },
        events
      }
    );
  }

  // Render
  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true }, attrs: this.loaderAttrs });
    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $promotionData: this.providerData(),
        $promotionMethods: this.providerMethods
      })
    );
  }
}
